import React, { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import {
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import PnlSummaryCards, { formatUsdSigned } from './pnl-summary-cards'
import PnlCurveChart from './pnl-curve-chart'
import PnlDailyBarChart from './pnl-daily-bar-chart'
import {
  type PnlDailyResponse,
  type PnlReconcileLatestResponse,
  type PnlV2Response,
  type PnlRange,
  RANGE_DESCRIPTIONS,
  RANGE_LABELS,
  RANGE_TO_GRANULARITY,
} from './pnl-types'

const RANGES: PnlRange[] = ['1d', '3d', '7d', '30d', '90d', 'all']

const API_BASE = process.env.REACT_APP_API_PNL_HISTORY ?? '/pnl_history'
const API_V2 = process.env.REACT_APP_API_PNL_HISTORY_V2 ?? `${API_BASE}/v2`
const API_DAILY = process.env.REACT_APP_API_PNL_DAILY ?? '/pnl_daily'
const API_RECONCILE = process.env.REACT_APP_API_PNL_RECONCILE ?? '/pnl_reconcile_latest'
const API_ACCOUNTS = process.env.REACT_APP_API_ACCOUNTS ?? '/accounts'

interface AccountsResponse {
  owners: string[]
}

function buildV2Url (range: PnlRange, selectedAccounts: string[], allAccounts: string[]): string {
  const granularity = RANGE_TO_GRANULARITY[range]
  const params = new URLSearchParams({ range, granularity })
  if (selectedAccounts.length > 0 && selectedAccounts.length < allAccounts.length) {
    params.set('accounts', selectedAccounts.join(','))
  }
  return `${API_V2}?${params.toString()}`
}

function buildDailyUrl (range: PnlRange, selectedAccounts: string[], allAccounts: string[]): string {
  const params = new URLSearchParams({ range })
  if (selectedAccounts.length > 0 && selectedAccounts.length < allAccounts.length) {
    params.set('accounts', selectedAccounts.join(','))
  }
  return `${API_DAILY}?${params.toString()}`
}

function fmtUtc (iso: string | null): string {
  if (iso === null) return '—'
  return `${iso.slice(0, 19).replace('T', ' ')} UTC`
}

// Reconcile status card: worst status + coverage + per-account breakdown in
// the tooltip (design §7.4). Retired accounts are excluded server-side via
// pnl_reconcile.py --skip-accounts.
const ReconcileBadge: React.FC = () => {
  const { data } = useSWR<PnlReconcileLatestResponse>(
    API_RECONCILE,
    basicFetcher,
    { refreshInterval: 300_000 }, // 5 min
  )

  if (data === undefined || data.rows.length === 0) {
    return (
      <Chip
        label='Not yet reconciled'
        size='small'
        sx={{ bgcolor: '#424242', color: '#b0b0b0' }}
      />
    )
  }

  const maxDiff = Math.max(...data.rows.map((r) => Math.abs(Number(r.diff))))
  const worst = data.rows.find((r) => r.status === 'error') ??
    data.rows.find((r) => r.status === 'warn') ??
    data.rows[0]

  const color = worst.status === 'error'
    ? '#f44336'
    : worst.status === 'warn'
      ? '#ff9800'
      : '#4caf50'
  const icon = worst.status === 'error' ? '✗' : worst.status === 'warn' ? '⚠' : '✓'
  const ts = fmtUtc(data.rows[0].ts)

  const detail = (
    <Box sx={{ fontFamily: 'monospace', fontSize: 12 }}>
      <div>Last run: {ts}</div>
      {data.rows.map((r) => (
        <div key={`${r.exchange}-${r.owner}`}>
          {r.status === 'error' ? '✗' : r.status === 'warn' ? '⚠' : '✓'}{' '}
          {r.owner}: {formatUsdSigned(Number(r.diff))}
          {r.note !== null && r.note !== '' ? ` (${r.note})` : ''}
        </div>
      ))}
    </Box>
  )

  return (
    <Tooltip title={detail} arrow>
      <Chip
        label={`Reconcile ${icon} ±$${maxDiff.toFixed(2)} · ${data.rows.length} acct`}
        size='small'
        sx={{ bgcolor: color, color: '#fff' }}
      />
    </Tooltip>
  )
}

const PnlPage: React.FC = () => {
  const [range, setRange] = useState<PnlRange>('7d')
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([])
  const [showDecomposition, setShowDecomposition] = useState<boolean>(false)

  const { data: accountsData } = useSWR<AccountsResponse>(API_ACCOUNTS, basicFetcher, {
    refreshInterval: 0, // accounts list rarely changes; revalidate only on focus/reconnect
  })
  const accounts = useMemo(() => accountsData?.owners ?? [], [accountsData])

  // First-time hydration: when accounts list arrives, default to "all selected".
  useEffect(() => {
    if (accounts.length > 0 && selectedAccounts.length === 0) {
      setSelectedAccounts(accounts)
    }
  }, [accounts, selectedAccounts.length])

  const url = useMemo(
    () => buildV2Url(range, selectedAccounts, accounts),
    [range, selectedAccounts, accounts],
  )
  const dailyUrl = useMemo(
    () => buildDailyUrl(range, selectedAccounts, accounts),
    [range, selectedAccounts, accounts],
  )

  const { data, error, isLoading } = useSWR<PnlV2Response>(
    url,
    basicFetcher,
    { refreshInterval: 30_000 },
  )
  const { data: dailyData } = useSWR<PnlDailyResponse>(
    range === '1d' ? null : dailyUrl,
    basicFetcher,
    { refreshInterval: 300_000 },
  )

  const series = data?.series ?? []
  const header = data?.header ?? null
  const dailyRows = dailyData?.rows ?? []
  const latestDailyRow = dailyRows[dailyRows.length - 1]
  const lastPoint = series.length > 0 ? series[series.length - 1] : null

  const equityCheckOk = header?.unreconciled_diff !== null &&
    header !== null && Math.abs(header.unreconciled_diff ?? 0) <= 0.01

  return (
    <Box sx={{ p: 3, backgroundColor: '#181818', minHeight: '100vh', color: '#e0e0e0' }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
        <Typography variant='h5'>PnL Dashboard</Typography>
        <ReconcileBadge />
      </Stack>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: '#1e1e1e' }}>
        <Stack direction='row' spacing={3} alignItems='center' flexWrap='wrap' useFlexGap>
          <Box>
            <Typography variant='caption' color='text.secondary'>Range</Typography>
            <ToggleButtonGroup
              value={range}
              exclusive
              size='small'
              onChange={(_e, v) => {
                if (v !== null) setRange(v)
              }}
              sx={{ ml: 1 }}
            >
              {RANGES.map((r) => (
                <ToggleButton key={r} value={r} sx={{ color: '#e0e0e0' }}>
                  {RANGE_LABELS[r]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography variant='caption' color='text.secondary' sx={{ mr: 1 }}>Accounts</Typography>
            <ToggleButtonGroup
              value={selectedAccounts}
              size='small'
              onChange={(_e, v) => {
                setSelectedAccounts(v as string[])
              }}
            >
              {accounts.map((a) => (
                <ToggleButton key={a} value={a} sx={{ color: '#e0e0e0' }}>
                  {a}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={showDecomposition}
                onChange={(_e, checked) => {
                  setShowDecomposition(checked)
                }}
              />
            }
            label='Show breakdown'
          />
        </Stack>

        {/* Actual window boundaries + data-quality flags (design §7.1) */}
        {header !== null && (
          <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap sx={{ mt: 1 }}>
            <Typography variant='caption' color='text.secondary'>
              {RANGE_DESCRIPTIONS[range]}: {fmtUtc(header.since)} → {fmtUtc(header.until)}
            </Typography>
            {header.data_quality === 'partial' && (
              <Tooltip
                arrow
                title={`Start boundary snapshot ${header.coverage.upnl_start.status}` +
                  (header.coverage.upnl_start.anchor_ts !== null
                    ? ` (sampled at ${fmtUtc(header.coverage.upnl_start.anchor_ts)})`
                    : '') +
                  ' — interval values may be incomplete near the window start.'}
              >
                <Chip label='partial coverage' size='small' sx={{ bgcolor: '#ff9800', color: '#000', height: 20 }} />
              </Tooltip>
            )}
            {header.approximate_exchanges.length > 0 && (
              <Tooltip
                arrow
                title={`${header.approximate_exchanges.join(', ')}: realized lands at position-cycle close, so windowed values are approximate (ALL range is exact).`}
              >
                <Chip label={`${header.approximate_exchanges.join(',')} approx`} size='small' sx={{ bgcolor: '#616161', color: '#e0e0e0', height: 20 }} />
              </Tooltip>
            )}
            {header.unreconciled_diff !== null && (
              <Tooltip
                arrow
                title={`Equity-method cross-check: equity Δ − transfers − component-sum = ${formatUsdSigned(header.unreconciled_diff)}. Within $0.01 means the flow ledger fully explains the equity move.`}
              >
                <Chip
                  label={`equity check ${equityCheckOk ? '✓' : formatUsdSigned(header.unreconciled_diff)}`}
                  size='small'
                  sx={{
                    bgcolor: equityCheckOk ? '#2e7d32' : '#ff9800',
                    color: equityCheckOk ? '#fff' : '#000',
                    height: 20,
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        )}
      </Paper>

      {/* Summary cards — six signed interval attribution items (design §7.2) */}
      <PnlSummaryCards header={header} />

      {/* Main chart: cumulative interval PnL, starts at $0, transfers excluded */}
      <Paper sx={{ p: 2, backgroundColor: '#1e1e1e' }}>
        <Typography variant='subtitle1' sx={{ mb: 1 }}>
          Cumulative Period PnL (transfers excluded)
        </Typography>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error !== undefined && !isLoading && (
          <Typography color='error'>Failed to load data, please retry.</Typography>
        )}
        {!isLoading && error === undefined && series.length === 0 && (
          <Typography color='text.secondary'>No data in this window.</Typography>
        )}
        {!isLoading && error === undefined && series.length > 0 && (
          <PnlCurveChart series={series} showDecomposition={showDecomposition} height={420} />
        )}
      </Paper>

      {/* Current position snapshot — separated from interval PnL (design §7.3) */}
      {header !== null && lastPoint !== null && (
        <Paper sx={{ p: 2, mt: 2, backgroundColor: '#1e1e1e' }}>
          <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
            Current position snapshot (as of {fmtUtc(header.coverage.upnl_end.anchor_ts)})
          </Typography>
          <Stack direction='row' spacing={4} flexWrap='wrap' useFlexGap>
            <Box>
              <Typography variant='caption' color='text.secondary'>Current unrealized PnL</Typography>
              <Typography sx={{ fontFamily: 'monospace', color: lastPoint.current_unrealized >= 0 ? '#4caf50' : '#f44336' }}>
                {formatUsdSigned(lastPoint.current_unrealized)}
              </Typography>
            </Box>
            <Box>
              <Typography variant='caption' color='text.secondary'>Unrealized Δ in window</Typography>
              <Typography sx={{ fontFamily: 'monospace', color: header.unrealized_change >= 0 ? '#4caf50' : '#f44336' }}>
                {formatUsdSigned(header.unrealized_change)}
              </Typography>
            </Box>
            {header.equity_end !== null && (
              <Box>
                <Typography variant='caption' color='text.secondary'>Account equity</Typography>
                <Typography sx={{ fontFamily: 'monospace', color: '#e0e0e0' }}>
                  ${header.equity_end.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {range !== '1d' && dailyRows.length > 0 && (
        <Paper sx={{ p: 2, mt: 2, backgroundColor: '#1e1e1e' }}>
          <Stack
            direction='row'
            alignItems='baseline'
            justifyContent='space-between'
            sx={{ mb: 1 }}
          >
            <Typography variant='subtitle1'>Daily PnL (UTC calendar days)</Typography>
            {latestDailyRow?.partial && (
              <Typography variant='caption' color='text.secondary'>
                Current day is partial
              </Typography>
            )}
          </Stack>
          <PnlDailyBarChart rows={dailyRows} height={260} />
        </Paper>
      )}

      {/* Transfer notice */}
      {header !== null && Math.abs(header.transfer) > 0.01 && (
        <Typography
          variant='caption'
          sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
        >
          Net transfers in window: {formatUsdSigned(header.transfer)} (excluded from Period PnL)
        </Typography>
      )}
    </Box>
  )
}

export default PnlPage
